
import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Save, Building, Mail, Globe, Users } from 'lucide-react';
import { toast } from 'sonner';
import { buildSettingsPayload } from '../utils/settingsPayload';

const UNASSIGNED = '__unassigned__';

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [staff, setStaff] = useState([]);
  const [staffLoading, setStaffLoading] = useState(true);
  const [staffError, setStaffError] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    company_email: '',
    company_phone: '',
    company_address: '',
    timezone: 'UTC',
    currency: 'USD',
    date_format: 'MM/DD/YYYY',
    notification_email: '',
    owner_user_id: '',
    supervisor_user_id: '',
    lead_user_id: ''
  });

  useEffect(() => {
    loadSettings();
    loadStaff();
  }, []);

  const loadStaff = async () => {
    setStaffLoading(true);
    setStaffError(false);
    try {
      const res = await api.get('/users');
      setStaff(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      // Non-fatal: keep any saved role assignments; just flag that the picker
      // list could not load (we never clear stored roles on a fetch failure).
      setStaff([]);
      setStaffError(true);
    } finally {
      setStaffLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
      setFormData({
        company_name: response.data.company_name || '',
        company_email: response.data.company_email || '',
        company_phone: response.data.company_phone || '',
        company_address: response.data.company_address || '',
        timezone: response.data.timezone || 'UTC',
        currency: response.data.currency || 'USD',
        date_format: response.data.date_format || 'MM/DD/YYYY',
        notification_email: response.data.notification_email || '',
        owner_user_id: response.data.owner_user_id || '',
        supervisor_user_id: response.data.supervisor_user_id || '',
        lead_user_id: response.data.lead_user_id || ''
      });
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const dataToSubmit = buildSettingsPayload(formData);

      await api.put('/settings', dataToSubmit);
      toast.success('Settings saved successfully');
      loadSettings();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <div className="co-page co-scope co-settings space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">Settings</h1>
        <p className="text-gray-600 mt-1">Configure system preferences and company information</p>
      </div>

      <Tabs defaultValue="company" className="space-y-4">
        <TabsList>
          <TabsTrigger value="company">Company Info</TabsTrigger>
          <TabsTrigger value="system">System Settings</TabsTrigger>
        </TabsList>

        <form onSubmit={handleSubmit}>
          <TabsContent value="company" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="h-5 w-5" />
                  Company Information
                </CardTitle>
                <CardDescription>Your business details and contact information</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="company_name">Company Name</Label>
                    <Input
                      id="company_name"
                      data-testid="company-name-input"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="company_email">Company Email</Label>
                    <Input
                      id="company_email"
                      type="email"
                      value={formData.company_email}
                      onChange={(e) => setFormData({ ...formData, company_email: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="company_phone">Company Phone</Label>
                    <Input
                      id="company_phone"
                      value={formData.company_phone}
                      onChange={(e) => setFormData({ ...formData, company_phone: e.target.value })}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="company_address">Company Address</Label>
                    <Input
                      id="company_address"
                      value={formData.company_address}
                      onChange={(e) => setFormData({ ...formData, company_address: e.target.value })}
                    />
                  </div>
                </div>

                <div className="pt-2 border-t" data-testid="company-roles-section">
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="h-4 w-4 text-neutral-500" />
                    <h3 className="text-sm font-semibold">Company Contacts</h3>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">Assign existing staff as the company Owner, Supervisor and Lead. Leave unassigned if not applicable.</p>
                  {staffLoading && (
                    <p className="text-xs text-gray-500 mb-3" data-testid="roles-staff-loading">Loading staff list…</p>
                  )}
                  {staffError && (
                    <p className="text-xs text-amber-600 mb-3" data-testid="roles-staff-error">Couldn't load the staff list. Any saved assignments below are preserved; try reloading to change them.</p>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      { key: 'owner_user_id', label: 'Owner' },
                      { key: 'supervisor_user_id', label: 'Supervisor' },
                      { key: 'lead_user_id', label: 'Lead' },
                    ].map(({ key, label }) => {
                      const currentId = formData[key];
                      const inList = currentId && staff.some((u) => u.id === currentId);
                      return (
                      <div key={key}>
                        <Label htmlFor={key}>{label}</Label>
                        <Select
                          value={currentId ? currentId : UNASSIGNED}
                          onValueChange={(value) => setFormData({ ...formData, [key]: value === UNASSIGNED ? '' : value })}
                        >
                          <SelectTrigger id={key} data-testid={`role-select-${key}`}>
                            <SelectValue placeholder="Unassigned" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={UNASSIGNED} data-testid={`role-option-unassigned-${key}`}>Unassigned</SelectItem>
                            {/* Preserve a saved assignment even if it isn't in the loaded list. */}
                            {currentId && !inList && (
                              <SelectItem value={currentId} data-testid={`role-option-current-${key}`}>
                                Current assignment (details unavailable)
                              </SelectItem>
                            )}
                            {staff.map((u) => (
                              <SelectItem key={u.id} value={u.id} data-testid={`role-option-${key}-${u.id}`}>
                                {u.full_name || u.email || u.id}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="system" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  System Preferences
                </CardTitle>
                <CardDescription>Regional and display settings</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="timezone">Timezone</Label>
                    <Select
                      value={formData.timezone}
                      onValueChange={(value) => setFormData({ ...formData, timezone: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="UTC">UTC</SelectItem>
                        <SelectItem value="America/New_York">Eastern Time</SelectItem>
                        <SelectItem value="America/Chicago">Central Time</SelectItem>
                        <SelectItem value="America/Denver">Mountain Time</SelectItem>
                        <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="currency">Currency</Label>
                    <Select
                      value={formData.currency}
                      onValueChange={(value) => setFormData({ ...formData, currency: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD ($)</SelectItem>
                        <SelectItem value="EUR">EUR (€)</SelectItem>
                        <SelectItem value="GBP">GBP (£)</SelectItem>
                        <SelectItem value="CAD">CAD ($)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="date_format">Date Format</Label>
                    <Select
                      value={formData.date_format}
                      onValueChange={(value) => setFormData({ ...formData, date_format: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem>
                        <SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem>
                        <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="notification_email">Notification Email</Label>
                    <Input
                      id="notification_email"
                      type="email"
                      value={formData.notification_email}
                      onChange={(e) => setFormData({ ...formData, notification_email: e.target.value })}
                      placeholder="alerts@company.com"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <div className="flex justify-end">
            <Button type="submit" disabled={saving} data-testid="save-settings-button">
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </form>
      </Tabs>
    </div>
  );
}
